import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import {
  getDomain,
  fetchCustomerData,
  fetchSeasons,
} from '../../utils/functions';
import Page from '../../components/Page';
import { Transaction } from '../../types/customer';
import { Season, SeasonsWithTransactions } from '../../types/drupal';
import Spinner from '../../components/Spinner';
import OrderItem from './OrderItem/OrderItem';
import OrderHistoryFilters from './OrderHistoryFilters';

interface Props {
  email: string;
  idToken: string;
}

dayjs.locale('fi');
const includedSources = ['iTicket', 'Rentle', 'Tekso'];

const OrderHistory: React.FC<Props> = (props: Props): JSX.Element => {
  const [transactions, setTransactions] = useState<Transaction[]>();
  const [fetchingCustomer, setFetchingCustomer] = useState(true);

  const [seasons, setSeasons] = useState<Season[]>();
  const [fetchingSeasons, setFetchingSeasons] = useState(true);

  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [keywords, setKeywords] = useState('');

  const today = dayjs().startOf('day');

  const domain = getDomain();
  const pageId: { [key: string]: string } = {};
  pageId['ruka'] = process.env.REACT_APP_RENTLE_PAGE_RUKA ?? '';
  pageId['pyha'] = process.env.REACT_APP_RENTLE_PAGE_PYHA ?? '';

  const { t, i18n } = useTranslation('order_history');
  const language = i18n.language;

  const noContentMessage = t('No bookings or purchases found', {
    ns: 'common',
  });

  useEffect(() => {
    async function getSeasons() {
      const seasonsData = await fetchSeasons(language);
      if (seasonsData) {
        setSeasons(seasonsData);
      }
      setFetchingSeasons(false);
    }

    getSeasons();
  }, []);

  useEffect(() => {
    async function getCustomer() {
      const response = await fetchCustomerData(
        props.idToken,
        false,
        'cdp,rentle',
      );

      if (response && response.transactions) {
        setTransactions(response.transactions);

        const productCategories: string[] = [];

        // Get all included product types.
        response.transactions.forEach((transaction) => {
          if (
            transaction.type &&
            !productCategories.includes(transaction.type) &&
            includedSources.includes(transaction.source)
          ) {
            productCategories.push(transaction.type);
          }
        });
        setCategories(productCategories);
      }

      setFetchingCustomer(false);
    }

    if (props.idToken) {
      getCustomer();
    }
  }, [props.idToken]);

  /**
   * Sort from newest to oldest.
   */
  function sortTransactions(a: Transaction, b: Transaction) {
    // If start date is available for a transaction, that should be what's shown,
    // so we'll also sort based on it if it's available.
    const aDate = dayjs(a.dateTime).valueOf();
    const bDate = dayjs(b.dateTime).valueOf();

    return bDate - aDate;
  }

  /**
   * Filter based on keywords and categories.
   */
  function filterTransactions(transaction: Transaction) {
    const isIncludedSource = includedSources.includes(transaction.source);

    let includedCategory = true;
    let includedKeywords = true;

    if (selectedCategories.length > 0) {
      includedCategory = selectedCategories.includes(transaction.type);
    }

    if (keywords) {
      includedKeywords = transaction.products.some((product) => {
        const productName = product.productName
          ? product.productName.toLowerCase()
          : '';

        return (
          productName.includes(keywords.toLowerCase()) ||
          (product.productGroup !== undefined &&
            product.productGroup !== null &&
            product.productGroup.toLowerCase().includes(keywords.toLowerCase()))
        );
      });
    }

    return includedKeywords && includedCategory && isIncludedSource;
  }

  let mainContent = <Spinner />;
  let secondaryContent = <Spinner />;

  const topContent = (
    <OrderHistoryFilters
      categories={categories}
      selectedCategories={selectedCategories}
      setSelectedCategories={setSelectedCategories}
      keywords={keywords}
      setKeywords={setKeywords}
    ></OrderHistoryFilters>
  );

  function getSeasonsForTransactions(
    transactions: Transaction[],
    seasonToFilter: Season[],
  ): SeasonsWithTransactions[] {
    let filteredSeasons: SeasonsWithTransactions[] = seasonToFilter.map(
      (season) => {
        return {
          ...season,
          transactions: [],
        };
      },
    );

    transactions.forEach((transaction) => {
      if (!transaction.dateTime) {
        return;
      }
      const transactionStart = dayjs(transaction.dateTime);

      const seasonIndex = filteredSeasons.findIndex((season) => {
        const seasonStart = dayjs(season.startDate);
        const seasonEnd = dayjs(season.endDate);

        return (
          (transactionStart.isAfter(seasonStart, 'day') ||
            transactionStart.isSame(seasonStart, 'day')) &&
          (transactionStart.isBefore(seasonEnd, 'day') ||
            transactionStart.isSame(seasonEnd, 'day'))
        );
      });

      if (seasonIndex >= 0) {
        filteredSeasons[seasonIndex]?.transactions.push(transaction);
      }
    });
    filteredSeasons = filteredSeasons.filter(
      (season) => season && season.transactions.length > 0,
    );

    return filteredSeasons ?? [];
  }

  if (!fetchingSeasons && !fetchingCustomer && seasons) {
    const currentAndFutureSeasons = seasons.filter((season) => {
      const seasonEnd = dayjs(season.endDate);

      return today.isBefore(seasonEnd, 'day') || today.isSame(seasonEnd, 'day');
    });

    const pastSeasons = seasons.filter((season) => {
      const seasonEnd = dayjs(season.endDate);
      return today.isAfter(seasonEnd, 'day');
    });

    if (transactions && currentAndFutureSeasons.length > 0) {
      const activeSeasons = getSeasonsForTransactions(
        transactions.filter(filterTransactions).sort(sortTransactions),
        currentAndFutureSeasons,
      );

      mainContent = (
        <>
          {activeSeasons.map((season) => (
            <OrderItem key={season.name} season={season} />
          ))}
        </>
      );
    } else {
      mainContent = <div>{noContentMessage}</div>;
    }

    if (transactions && pastSeasons.length > 0) {
      const oldSeasons = getSeasonsForTransactions(
        transactions.filter(filterTransactions).sort(sortTransactions),
        pastSeasons,
      );

      secondaryContent = (
        <>
          {oldSeasons.map((season) => (
            <OrderItem key={season.name} season={season} />
          ))}
        </>
      );
    } else {
      secondaryContent = <div></div>;
    }
  }

  return (
    <>
      <Page
        pageId={pageId[domain]}
        topContent={topContent}
        mainContent={mainContent}
        secondaryContent={secondaryContent}
      />
    </>
  );
};

export default OrderHistory;
